import type { TurnStep } from '../core/combat';

/**
 * Plays an enemy turn one beat at a time.
 *
 * Combat has already resolved everything by the time these steps exist — this
 * is pure presentation. It exists because an enemy turn that resolves in a
 * single frame reads as "nothing happened": the HP bar moves 7% and the only
 * visible change is the intent text. Walking the beats makes each threat land.
 */
export interface TurnPlayback {
  readonly steps: readonly TurnStep[];
  index: number;
  t: number;
  /** Set once the current step's fx have been emitted. */
  emitted: boolean;
}

export function startTurnPlayback(steps: readonly TurnStep[]): TurnPlayback | null {
  if (steps.length === 0) return null;
  return { steps, index: 0, t: 0, emitted: false };
}

/** Advance. Returns true at the moment a new step becomes current. */
export function tickTurnPlayback(p: TurnPlayback, dt: number): boolean {
  const cur = p.steps[p.index];
  if (!cur) return false;
  p.t += dt;
  if (p.t < cur.dur) return false;

  p.t -= cur.dur;
  p.index += 1;
  p.emitted = false;
  return p.index < p.steps.length;
}

export function turnPlaybackDone(p: TurnPlayback): boolean {
  return p.index >= p.steps.length;
}

export function currentStep(p: TurnPlayback): TurnStep | null {
  return p.steps[p.index] ?? null;
}

/** 0→1 progress through the current step. Drives the falling piece. */
export function stepProgress(p: TurnPlayback): number {
  const cur = p.steps[p.index];
  if (!cur || cur.dur <= 0) return 1;
  return Math.min(1, p.t / cur.dur);
}

export function turnPlaybackDuration(steps: readonly TurnStep[]): number {
  let total = 0;
  for (const s of steps) total += s.dur;
  return total;
}
